// src/components/Leads/EditLeadDrawer.jsx
import React, { useState, useEffect } from "react";
import api from "../../utils/api";

export default function EditLeadDrawer({ open, onClose, leadId, onLeadUpdated }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sources, setSources] = useState([]);
  const [staff, setStaff] = useState([]);

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    source_id: "",
    assigned_to: "",
    notes: "",
    product_service: "",
    budget_range: "",
  });

  // Load lead data + dropdowns when drawer opens
  useEffect(() => {
    if (!open || !leadId) return;

    async function loadData() {
      setLoading(true);
      try {
        const [leadRes, sourcesRes, usersRes] = await Promise.all([
          api.get(`/leads/${leadId}`),
          api.get("/leads/sources"),
          api.get("/users"),
        ]);

        const lead = leadRes.data.lead || leadRes.data;

        setForm({
          name: lead.name || "",
          mobile: lead.mobile || "",
          email: lead.email || "",
          source_id: lead.source_id || "",
        //   assigned_to: lead.assigned_to || "",
          notes: lead.notes || "",
          product_service: lead.product_service || "",
          budget_range: lead.budget_range || "",
        });

        setSources(sourcesRes.data || []);

        const allowedStaff = usersRes.data.users.filter((u) =>
          ["Sales", "TeamLead", "Admin", "SuperAdmin"].includes(u.role)
        );
        setStaff(allowedStaff);
      } catch (err) {
        console.error("Failed to load lead for edit:", err);
        alert("Could not load lead data");
        onClose();
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [open, leadId, onClose]);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.mobile.trim()) {
      alert("Name and Mobile are required");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/leads/${leadId}`, form);
      alert("Lead updated successfully!");
      onLeadUpdated(); // Refresh list
      onClose();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-50 ${open ? "pointer-events-auto" : "pointer-events-none"}`}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          open ? "opacity-50" : "opacity-0"
        }`}
        onClick={() => !saving && onClose()}
      />

      {/* Drawer */}
      <div
        className={`absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl transform transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Edit Lead</h2>
            <button
              onClick={() => !saving && onClose()}
              disabled={saving}
              className="p-2 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-orange-600"></div>
          </div>
        ) : (
          <div className="p-8 space-y-8 overflow-y-auto h-full pb-32">
            {/* Form fields – almost identical to LeadCreate */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter 10-digit mobile number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="customer@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
                  <select
                    value={form.source_id}
                    onChange={(e) => setForm({ ...form, source_id: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
                  >
                    <option value="">Select Source</option>
                    {sources.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-6">
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assign To</label>
                  <select
                    value={form.assigned_to}
                    onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
                  >
                    <option value="">Unassigned</option>
                    {staff.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </option>
                    ))}
                  </select>
                </div> */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product / Service Interested
                  </label>
                  <input
                    type="text"
                    value={form.product_service}
                    onChange={(e) => setForm({ ...form, product_service: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="e.g. Interior Work, Modular Kitchen"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
                  <input
                    type="text"
                    value={form.budget_range}
                    onChange={(e) => setForm({ ...form, budget_range: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="e.g. ₹2-5 Lakhs"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    rows={4}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                    placeholder="Additional remarks..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-6">
          <div className="flex justify-end gap-4">
            <button
              onClick={() => !saving && onClose()}
              disabled={saving}
              className="px-8 py-3.5 text-base font-medium text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || loading}
              className="px-10 py-3.5 text-base font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-xl shadow-md transition disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}